import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  flag: string;
  rateAgainstUSD: number; // Conversion rate relative to USD (USD = 1.0)
  symbolPosition?: 'before' | 'after';
  decimals?: number;
}

@Injectable({
  providedIn: 'root'
})
export class CurrencyService {
  private readonly STORAGE_KEY = 'antiqry_active_currency';

  // Comprehensive directory of global currencies with real-world baseline rates
  public readonly currencies: CurrencyInfo[] = [
    { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧', rateAgainstUSD: 0.77, symbolPosition: 'before', decimals: 0 },
    { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺', rateAgainstUSD: 0.92, symbolPosition: 'before', decimals: 0 },
    { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸', rateAgainstUSD: 1.00, symbolPosition: 'before', decimals: 0 },
    { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar', flag: '🇨🇦', rateAgainstUSD: 1.38, symbolPosition: 'before', decimals: 0 },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺', rateAgainstUSD: 1.52, symbolPosition: 'before', decimals: 0 },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵', rateAgainstUSD: 151.5, symbolPosition: 'before', decimals: 0 },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', flag: '🇨🇭', rateAgainstUSD: 0.88, symbolPosition: 'before', decimals: 0 },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳', rateAgainstUSD: 7.24, symbolPosition: 'before', decimals: 0 },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳', rateAgainstUSD: 84.1, symbolPosition: 'before', decimals: 0 },
    { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', flag: '🇸🇬', rateAgainstUSD: 1.32, symbolPosition: 'before', decimals: 0 },
    { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', flag: '🇳🇿', rateAgainstUSD: 1.68, symbolPosition: 'before', decimals: 0 },
    { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar', flag: '🇭🇰', rateAgainstUSD: 7.78, symbolPosition: 'before', decimals: 0 },
    { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', flag: '🇸🇪', rateAgainstUSD: 10.6, symbolPosition: 'after', decimals: 0 },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', flag: '🇳🇴', rateAgainstUSD: 10.9, symbolPosition: 'after', decimals: 0 },
    { code: 'DKK', symbol: 'kr.', name: 'Danish Krone', flag: '🇩🇰', rateAgainstUSD: 6.87, symbolPosition: 'after', decimals: 0 },
    { code: 'PLN', symbol: 'zł', name: 'Polish Zloty', flag: '🇵🇱', rateAgainstUSD: 4.02, symbolPosition: 'after', decimals: 0 },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', flag: '🇧🇷', rateAgainstUSD: 5.75, symbolPosition: 'before', decimals: 0 },
    { code: 'MXN', symbol: 'Mex$', name: 'Mexican Peso', flag: '🇲🇽', rateAgainstUSD: 20.0, symbolPosition: 'before', decimals: 0 },
    { code: 'AED', symbol: 'AED', name: 'UAE Dirham', flag: '🇦🇪', rateAgainstUSD: 3.67, symbolPosition: 'before', decimals: 0 },
    { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal', flag: '🇸🇦', rateAgainstUSD: 3.75, symbolPosition: 'before', decimals: 0 },
    { code: 'ZAR', symbol: 'R', name: 'South African Rand', flag: '🇿🇦', rateAgainstUSD: 17.6, symbolPosition: 'before', decimals: 0 },
    { code: 'TRY', symbol: '₺', name: 'Turkish Lira', flag: '🇹🇷', rateAgainstUSD: 34.3, symbolPosition: 'before', decimals: 0 },
    { code: 'ILS', symbol: '₪', name: 'Israeli Shekel', flag: '🇮🇱', rateAgainstUSD: 3.72, symbolPosition: 'before', decimals: 0 },
    { code: 'THB', symbol: '฿', name: 'Thai Baht', flag: '🇹🇭', rateAgainstUSD: 33.8, symbolPosition: 'before', decimals: 0 },
    { code: 'KRW', symbol: '₩', name: 'South Korean Won', flag: '🇰🇷', rateAgainstUSD: 1380.0, symbolPosition: 'before', decimals: 0 },
    { code: 'TWD', symbol: 'NT$', name: 'New Taiwan Dollar', flag: '🇹🇼', rateAgainstUSD: 32.1, symbolPosition: 'before', decimals: 0 }
  ];

  // Country name to Currency code dictionary covering global territories
  private readonly countryToCurrencyMap: Record<string, string> = {
    'united kingdom': 'GBP',
    'uk': 'GBP',
    'great britain': 'GBP',
    'england': 'GBP',
    'scotland': 'GBP',
    'wales': 'GBP',
    'northern ireland': 'GBP',
    'isle of man': 'GBP',
    'jersey': 'GBP',
    'guernsey': 'GBP',
    'united states': 'USD',
    'usa': 'USD',
    'us': 'USD',
    'canada': 'CAD',
    'australia': 'AUD',
    'new zealand': 'NZD',
    'japan': 'JPY',
    'switzerland': 'CHF',
    'china': 'CNY',
    'india': 'INR',
    'singapore': 'SGD',
    'hong kong': 'HKD',
    'hong kong sar china': 'HKD',
    'sweden': 'SEK',
    'norway': 'NOK',
    'denmark': 'DKK',
    'poland': 'PLN',
    'brazil': 'BRL',
    'mexico': 'MXN',
    'united arab emirates': 'AED',
    'saudi arabia': 'SAR',
    'south africa': 'ZAR',
    'turkey': 'TRY',
    'israel': 'ILS',
    'thailand': 'THB',
    'south korea': 'KRW',
    'taiwan': 'TWD',

    // Eurozone Countries
    'austria': 'EUR',
    'belgium': 'EUR',
    'croatia': 'EUR',
    'cyprus': 'EUR',
    'estonia': 'EUR',
    'finland': 'EUR',
    'france': 'EUR',
    'germany': 'EUR',
    'greece': 'EUR',
    'ireland': 'EUR',
    'italy': 'EUR',
    'latvia': 'EUR',
    'lithuania': 'EUR',
    'luxembourg': 'EUR',
    'malta': 'EUR',
    'netherlands': 'EUR',
    'portugal': 'EUR',
    'slovakia': 'EUR',
    'slovenia': 'EUR',
    'spain': 'EUR',
    'monaco': 'EUR',
    'san marino': 'EUR',
    'vatican city': 'EUR',
    'andorra': 'EUR'
  };

  private currentCurrencySubject: BehaviorSubject<CurrencyInfo>;
  public currentCurrency$: Observable<CurrencyInfo>;

  constructor() {
    const initial = this.detectInitialCurrency();
    this.currentCurrencySubject = new BehaviorSubject<CurrencyInfo>(initial);
    this.currentCurrency$ = this.currentCurrencySubject.asObservable();
  }

  public get currentCurrency(): CurrencyInfo {
    return this.currentCurrencySubject.value;
  }

  /**
   * Sets the user's preferred active currency and persists it to localStorage.
   */
  public setCurrency(code: string): void {
    const found = this.getCurrency(code);
    this.currentCurrencySubject.next(found);
    try {
      localStorage.setItem(this.STORAGE_KEY, found.code);
    } catch (e) {
      console.warn('Could not persist currency to localStorage', e);
    }
  }

  /**
   * Get currency details by code. Fallback to GBP or USD if not found.
   */
  public getCurrency(code?: string): CurrencyInfo {
    if (!code) return this.currentCurrency;
    const upper = code.trim().toUpperCase();
    const match = this.currencies.find(c => c.code === upper);
    if (match) return match;

    // Fallback if unknown code
    return {
      code: upper,
      symbol: upper + ' ',
      name: upper,
      flag: '🌐',
      rateAgainstUSD: 1.0,
      symbolPosition: 'before',
      decimals: 0
    };
  }

  /**
   * Returns the appropriate currency symbol for a currency code.
   */
  public getSymbol(code?: string): string {
    return this.getCurrency(code).symbol;
  }

  /**
   * Detects the national currency associated with a given country name or code.
   */
  public getCurrencyForCountry(country?: string): CurrencyInfo {
    if (!country) return this.currentCurrency;
    const clean = country.trim().toLowerCase();
    const mappedCode = this.countryToCurrencyMap[clean];
    if (mappedCode) {
      return this.getCurrency(mappedCode);
    }

    // Try partial match
    for (const [key, code] of Object.entries(this.countryToCurrencyMap)) {
      if (clean.includes(key) || key.includes(clean)) {
        return this.getCurrency(code);
      }
    }

    return this.currentCurrency;
  }

  /**
   * Converts an amount from one currency to another using the rate directory.
   */
  public convert(amount: number, fromCode: string, toCode: string): number {
    if (!amount || fromCode === toCode) return amount || 0;
    const from = this.getCurrency(fromCode);
    const to = this.getCurrency(toCode);

    // Convert from source to USD, then from USD to target
    const inUSD = amount / (from.rateAgainstUSD || 1.0);
    const inTarget = inUSD * (to.rateAgainstUSD || 1.0);
    return Math.round(inTarget * 100) / 100;
  }

  /**
   * Formats a numeric price into a luxury formatted currency string (e.g. £1,250, €1,450, $1,600).
   * If convertToActive is true, converts from sourceCurrency to active display currency.
   */
  public format(
    amount: number | null | undefined, 
    sourceCurrency?: string, 
    convertToActive: boolean = false
  ): string {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '';
    }

    const src = this.getCurrency(sourceCurrency || this.currentCurrency.code);
    const target = convertToActive ? this.currentCurrency : src;
    const finalAmount = convertToActive ? this.convert(amount, src.code, target.code) : amount;

    const hasCents = Math.abs(finalAmount - Math.round(finalAmount)) > 0.001;
    const decimals = hasCents ? 2 : (target.decimals ?? 0);

    const formattedNumber = new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(finalAmount);

    if (target.symbolPosition === 'after') {
      return `${formattedNumber} ${target.symbol}`;
    }
    return `${target.symbol}${formattedNumber}`;
  }

  /**
   * Initial detection based on localStorage, browser locale, or default.
   */
  private detectInitialCurrency(): CurrencyInfo {
    // 1. Saved preference in localStorage
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        const found = this.currencies.find(c => c.code === saved.toUpperCase());
        if (found) return found;
      }
    } catch (e) {}

    // 2. Browser locale detection
    try {
      const navLang = (navigator.language || (navigator as any).userLanguage || '').toLowerCase();
      if (navLang.includes('gb') || navLang === 'en-uk') {
        return this.getCurrency('GBP');
      }
      if (navLang.includes('us')) {
        return this.getCurrency('USD');
      }
      if (navLang.includes('ca')) {
        return this.getCurrency('CAD');
      }
      if (navLang.includes('au')) {
        return this.getCurrency('AUD');
      }
      if (navLang.includes('jp') || navLang.startsWith('ja')) {
        return this.getCurrency('JPY');
      }
      if (navLang.includes('ch')) {
        return this.getCurrency('CHF');
      }
      if (['de', 'fr', 'es', 'it', 'nl', 'pt', 'el', 'at', 'be', 'fi'].some(prefix => navLang.startsWith(prefix) || navLang.includes(prefix))) {
        return this.getCurrency('EUR');
      }
    } catch (e) {}

    // Default to GBP for Antiqry
    return this.getCurrency('GBP');
  }
}
